// Composition root for the authenticated app. Given a ready account, its
// identity keys, a token accessor and the shared KV store, it constructs the
// real call engine (with browser adapters), media control, echo test and REST
// client, and bundles them into the `ScreensDeps` J9 consumes. Nothing here
// starts the network — `engine.start()` is the master tab's job (J10).
//
// It is also where the web host's DIAGNOSTICS EVENTS are cut (docs/10 §2). The
// brain owns diagnostics end to end — the ring, redaction, batching, the
// delivery ladder — but it only owns what it is TOLD, and on this platform
// nothing was telling it: `DiagCenter.record` had no caller anywhere in the web
// app, so `capture()` answered null, the preview was permanently empty and the
// auto-send toggle governed a path that could not fire. The transport facts
// §2 names are witnessed here, in the composition root, for the same reason
// `runtime/core.ts` witnesses them for iOS and Android: this is where the fetch,
// the token minter and the engine's persist hook all pass through one function.
// Nothing is redacted or batched here — every value goes through a `diag/events`
// constructor, which is the thing that makes "a token cannot reach a report" a
// property of the types rather than a promise.

import type { EndpointList, WsFrame } from '@merkury/protocol';
import { CallEngine, EchoTest, MediaControl, type ContactsProvider, type IdentitySigner } from '@merkury/brain/call/index.js';
import { createIceServerProvider } from '@merkury/brain/call/iceServers.js';
import { LiveEvents, RestClient, type ScreensDeps } from '../screens/index.js';
import { signWithIdentity, type IdentityKeys } from '@merkury/brain/identity/keys.js';
import { defaultFetch, httpFailureKind } from '@merkury/brain/lib/http.js';
import {
  NETWORK_KINDS,
  endpointWon,
  httpAttempt,
  netChange,
  statusOutcome,
  tokenRefresh,
  type DiagCenter,
  type DiagEvent,
  type HttpOutcome,
  type HttpStatusOutcome,
  type NetworkKind,
} from '@merkury/brain/runtime/diag/index.js';
import type { AccessTokenOptions, AccessTokenProvider } from '@merkury/brain/call/types.js';
import { noteWebCall, webDiagWiring } from '../screens/settings/diagnostics/gateway.js';
import { deriveAliasKey } from '@merkury/brain/identity/aliasBlob.js';
import { AliasSyncDriver } from '@merkury/brain/aliasSync/index.js';
import type { StoredAccount } from '@merkury/brain/storage/accountStore.js';
import {
  getAliasSyncMarker,
  getAliases,
  getUnseenMissedCount,
  onAliasesChanged,
  recordEndedCall,
  replaceAliases,
  setAliasSyncMarker,
  subscribeCallLog,
} from '../storage/kvStores.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';
import { getCachedFriends } from '../storage/serverCache.js';
import { isCapacitorNative } from './capacitor/platform.js';
import {
  browserEchoTestDeps,
  browserMediaAdapter,
  attachRemoteAudio,
  browserMediaControlDeps,
  browserPeerFactory,
  browserWebSocketFactory,
  kvEndpointListStorage,
  kvKeyPinStorage,
} from './browserAdapters.js';
import { refreshEndpoints } from '@merkury/brain/runtime/endpointsRefresh.js';
import { routeFailures } from '@merkury/brain/api/client.js';
import { API_BASE_URL, ENDPOINTS_MIRROR_URL } from './config.js';
import { devEndpointList } from './devServer.js';
import { bootstrapEndpointList, endpointKeyring } from './endpoints.js';
import { noteShellEndpointState, shellRestOrigin } from './restOrigin.js';

export interface AppServices {
  engine: CallEngine;
  media: MediaControl;
  echoTest: EchoTest;
  rest: RestClient;
  liveEvents: LiveEvents;
  screensDeps: ScreensDeps;
  /** Stop the engine and release the mic. Idempotent. */
  dispose: () => void;
}

// --- diagnostics (docs/10 §§1-2) --------------------------------------------

/**
 * The process-wide diagnostics centre, reached WITHOUT making boot wait for it.
 *
 * `webDiagWiring()` opens the device KV and restores the persisted ring, and
 * boot must not block on that (prime directive: nothing cosmetic gets in front
 * of the connection) — so every event goes through the memoized wiring promise
 * and lands one microtask later. Deliberately NOT cached in a local: the
 * gateway owns the centre's lifetime, a cached handle would go on writing to a
 * replaced one, and every payload below is computed before the hop, so the hop
 * costs an ordering-preserving microtask and nothing else. It is the SAME
 * centre the Developer screen drives: a second one would mean two report queues
 * writing one KV slot.
 *
 * Never throws and never rejects: diagnostics observing a path may not be a way
 * for that path to fail.
 */
function withCentre(fn: (centre: DiagCenter) => void): void {
  void webDiagWiring()
    .then((wiring) => {
      try {
        fn(wiring.centre);
      } catch {
        // A broken sink must not take down the request that was reporting to it.
      }
    })
    .catch(() => undefined);
}

function recordDiag(event: DiagEvent): void {
  withCentre((centre) => centre.record(event));
}

/**
 * A thrown transport failure as an outcome KIND (docs/10 §3: kinds, never raw
 * error strings — the text is written by the browser and can name the host).
 * Mirrors `runtime/core.ts`'s private helper of the same name, because the two
 * hosts must classify identically; it belongs in `diag/events.ts`, which this
 * job does not own (see the report).
 */
function httpOutcomeOf(err: unknown): HttpOutcome {
  const kind = httpFailureKind(err);
  if (kind === 'timeout') return 'timeout';
  if (kind === 'rejected') return 'rejected';
  if (kind === 'frozen') return 'frozen';
  if (kind === 'cancelled') return 'cancelled';
  return 'other';
}

/**
 * The transport every REST/auth byte of the web shell rides, decorated so each
 * request leaves an `http.attempt` behind: host, route TEMPLATE, method, how it
 * ended and how long it took. This is the "slow vs blocked vs refused, per
 * origin" axis the 2026-07-26 incident had no way to read.
 *
 * The decoration sits OUTSIDE the resilient fetch, exactly as in
 * `runtime/core.ts`, so one event describes one request rather than one leg of
 * its retry chain. The diagnostics UPLOADER deliberately does not ride this one
 * (`gateway.ts` builds its own): routing an upload through it would make every
 * failed upload the content of the next report, which fails the same way.
 */
const shellFetch: typeof fetch = (() => {
  const raw = defaultFetch();
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === 'string' ? input : String((input as { url?: unknown }).url ?? input);
    const method = (init?.method ?? 'GET').toUpperCase();
    const startedAt = Date.now();
    const note = (outcome: HttpOutcome | HttpStatusOutcome): void => {
      recordDiag(
        httpAttempt({
          url,
          path: url,
          method,
          attempt: 1,
          elapsedMs: Date.now() - startedAt,
          outcome,
        }),
      );
    };
    try {
      const response = await raw(input, init);
      note(response.ok ? 'ok' : statusOutcome(response.status));
      return response;
    } catch (err) {
      note(httpOutcomeOf(err));
      throw err;
    }
  };
})();

/**
 * Decorate the session's token provider so each ACQUISITION leaves a
 * `token.refresh` behind (docs/10 §2). Same two rules `runtime/core.ts` states
 * and for the same reasons: `opts` is forwarded (the signaling client asks for
 * `{forceFresh:true}` after a WS 4001/4403 and a wrapper that swallowed it would
 * hand back the already-rejected token forever), and a CACHE HIT is not a
 * refresh — every REST request awaits `getToken()`, so recording those would
 * bury the 15-minute fuse this event exists to expose. Nothing is retried here:
 * `POST /auth/refresh` consumes its refresh token and a replay on any path kills
 * the session family (docs/03 §3).
 */
function timedToken(inner: () => Promise<string>): AccessTokenProvider {
  let lastToken: string | null = null;
  return async (opts?: AccessTokenOptions): Promise<string> => {
    const startedAt = Date.now();
    const trigger =
      opts?.forceFresh === true ? 'unauthorized' : lastToken === null ? 'boot' : 'expiry';
    try {
      const token = await (inner as AccessTokenProvider)(opts);
      const refreshed = token !== lastToken;
      lastToken = token;
      if (refreshed) {
        recordDiag(tokenRefresh({ trigger, outcome: 'ok', elapsedMs: Date.now() - startedAt }));
      }
      return token;
    } catch (err) {
      recordDiag(
        tokenRefresh({ trigger, outcome: httpOutcomeOf(err), elapsedMs: Date.now() - startedAt }),
      );
      throw err;
    }
  };
}

/** The brain's closed `net.kind` vocabulary, as the browser can answer it. */
const BRAIN_NETWORK_KINDS: ReadonlySet<string> = new Set<string>(NETWORK_KINDS);

/**
 * The coarse network kind, from the two facts a browser will state: `onLine`
 * and `navigator.connection`. `effectiveType` (`4g`, `slow-2g`) is outside the
 * brain's vocabulary and honestly answers `unknown` rather than being mapped
 * onto a link type it does not describe.
 */
function currentNetworkKind(): NetworkKind {
  const nav = (
    globalThis as {
      navigator?: { onLine?: boolean; connection?: { type?: string; effectiveType?: string } };
    }
  ).navigator;
  if (nav?.onLine === false) return 'none';
  const kind = nav?.connection?.type ?? nav?.connection?.effectiveType;
  return typeof kind === 'string' && BRAIN_NETWORK_KINDS.has(kind)
    ? (kind as NetworkKind)
    : 'unknown';
}

/** Friend directory backed by `GET /friends`, cached briefly to avoid churn. */
function restContactsProvider(rest: RestClient): ContactsProvider {
  let cache: { at: number; keys: Map<string, string> } | null = null;
  const TTL_MS = 15_000;

  /** Keys from the persisted friends snapshot — survives process death, which
   *  the in-memory cache above does not. Android kills the WebView constantly,
   *  so without this a cold, offline app cannot place a call at all (docs/00
   *  offline-first; prime directive: never fail a call we could have made). */
  const persisted = (): Map<string, string> | null => {
    const snapshot = getCachedFriends();
    if (!snapshot) return null;
    return new Map(snapshot.data.map((f) => [f.userId, f.publicKey]));
  };

  return {
    async getPublicKey(userId) {
      const now = Date.now();
      if (!cache || now - cache.at > TTL_MS) {
        try {
          const { friends } = await rest.listFriends();
          cache = { at: now, keys: new Map(friends.map((f) => [f.userId, f.publicKey])) };
        } catch {
          // Offline: fall back to whatever we last knew (prime directive).
          if (!cache) return persisted()?.get(userId) ?? null;
        }
      }
      return cache.keys.get(userId) ?? persisted()?.get(userId) ?? null;
    },
  };
}

/**
 * Push the unseen-missed count to the native launcher badge. No-op off native;
 * the plugin module is dynamically imported (behind isCapacitorNative) so the
 * web bundle never pulls in @capacitor/core. Never throws — the badge is
 * cosmetic and must not touch the call path (prime directive).
 */
async function pushMissedBadge(count: number): Promise<void> {
  if (!isCapacitorNative()) return;
  try {
    const { nativePlugins } = await import('./capacitor/plugins.js');
    await nativePlugins().callNotification.setMissedBadge({ count });
  } catch {
    // Missing plugin / bridge error — the launcher badge just won't update.
  }
}

/**
 * Keep a badge sink in sync with the device-local unseen-missed count: pushes
 * the current count now and on every call-log change (a new incoming missed
 * call, or Recents clearing them via markAllMissedSeen). Deduped, so a log
 * write that leaves the count unchanged is silent. Returns an unsubscribe.
 * The sink is injected so the wiring is testable without the native bridge.
 */
export function wireMissedBadge(setBadge: (count: number) => void): () => void {
  let last: number | null = null;
  const push = (): void => {
    const count = getUnseenMissedCount();
    if (count === last) return;
    last = count;
    setBadge(count);
  };
  push();
  return subscribeCallLog(push);
}

export async function createAppServices(input: {
  account: StoredAccount;
  identity: IdentityKeys;
  getAccessToken: () => Promise<string>;
  kv: KvStore;
}): Promise<AppServices> {
  const { account, identity, getAccessToken, kv } = input;
  // Every acquisition of an access token becomes a `token.refresh` event, and
  // the one wrapper is shared by REST, the engine and the endpoint poll so the
  // 15-minute fuse is measured once rather than three times.
  const getToken = timedToken(getAccessToken);
  // The endpoint-list refresh runs from `ReadyApp` with the engine alone; this
  // is where the authenticated session it needs becomes available.
  sessionToken = getToken;
  // Open the diagnostics centre on THIS session's KV — one handle, one owner
  // (diagnostics/gateway.ts, first call wins). Not awaited: boot never waits on
  // a storage round trip for something that only records.
  void webDiagWiring({ kv }).catch(() => undefined);

  const signer: IdentitySigner = {
    userId: account.userId ?? '',
    publicKeyB64: identity.publicKeyB64,
    sign: (data) => signWithIdentity(identity.seed, data),
  };

  // Every REST byte goes through the shell's one origin router (restOrigin.ts,
  // docs/04 §2): the build-time base to start with, re-pointed at whatever the
  // signaling race actually won. The same router already carries auth (it was
  // built before this session existed) and the healing `/endpoints` poll, so
  // there is exactly one answer to "which host answers us on this network".
  const rest = new RestClient({ baseUrl: shellRestOrigin(), getToken, fetchFn: shellFetch });
  const liveEvents = new LiveEvents();
  const contacts = restContactsProvider(rest);

  // Retries + TTL cache: a single failed fetch on a doze-woken phone must not
  // strip the call of STUN/TURN (field 2026-07-19 "connecting → failed, but
  // immediate call-back works").
  const getIceServers = createIceServerProvider({
    fetchCredentials: () => rest.turnCredentials(),
  });

  const endpointStorage = kvEndpointListStorage(kv);
  const persisted = await endpointStorage.load().catch(() => null);
  // The WS bootstrap follows the same server the REST base points at: an
  // absolute baked API base (APK builds — the WebView origin is localhost)
  // beats the serving origin (web PWA, same-origin).
  const apiHost = ((): { host: string; secure: boolean } => {
    try {
      const u = new URL(API_BASE_URL);
      return { host: u.host, secure: u.protocol === 'https:' };
    } catch {
      return { host: location.host, secure: location.protocol === 'https:' };
    }
  })();
  // A dev-mode backend override wins over any persisted/prod list.
  const endpointList: EndpointList =
    devEndpointList() ??
    persisted?.list ??
    bootstrapEndpointList(apiHost.host, apiHost.secure);

  // Re-assert the EFFECTIVE list — a dev override or the bootstrap floor is not
  // what the cold seed in restOrigin.ts read out of storage — together with the
  // winner a previous session proved, which is the only evidence a cold start
  // has about this network. A winner the effective list no longer names is
  // dropped there, not here.
  noteShellEndpointState({
    list: endpointList,
    ...(persisted?.lastGoodUrl ? { lastGoodUrl: persisted.lastGoodUrl } : {}),
  });

  // "Which endpoint the race actually won" is THE fact the 2026-07-26 report
  // could not answer, because nothing logged it (docs/10 §2). The engine's
  // persist hook is where a win becomes observable, so it is where the event is
  // cut: `round` counts wins this process, `elapsedMs` runs from the moment
  // signaling last started trying (reset below when the socket drops).
  let winnerWsUrl: string | null = persisted?.lastGoodUrl ?? null;
  let raceRound = 0;
  let racingSince = Date.now();

  const engine = new CallEngine({
    identity: signer,
    contacts,
    pinStorage: kvKeyPinStorage(kv),
    keyring: endpointKeyring(),
    endpointList,
    ...(persisted?.lastGoodUrl ? { lastGoodUrl: persisted.lastGoodUrl } : {}),
    // The one hook that fires on both a new race winner and a newly adopted
    // list — so REST re-points the moment signaling proves what works.
    persistEndpoints: (state) => {
      noteShellEndpointState(state);
      void endpointStorage.save(state);
      if (state.lastGoodUrl !== undefined && state.lastGoodUrl !== winnerWsUrl) {
        winnerWsUrl = state.lastGoodUrl;
        raceRound += 1;
        recordDiag(
          endpointWon({
            url: state.lastGoodUrl,
            round: raceRound,
            elapsedMs: Date.now() - racingSince,
          }),
        );
      }
    },
    getToken,
    getIceServers,
    ws: browserWebSocketFactory,
    pc: browserPeerFactory,
    media: browserMediaAdapter,
    onFrame: (frame: WsFrame) => liveEvents.handleFrame(frame),
  });

  const media = new MediaControl(browserMediaControlDeps());
  const echoTest = new EchoTest(browserEchoTestDeps());

  // Peer audio: keep the hidden sink fed with the engine's remoteStream.
  const detachRemoteAudio = attachRemoteAudio(engine.state);

  // Call history is device-local (docs/02 §1): append an entry the moment a
  // call ends. `lastEnded` carries its own `connectedAt`, so classification is
  // robust even when `call` was cleared before `lastEnded` landed;
  // recordEndedCall is idempotent per callId.
  let prevLastEnded = engine.state.get().lastEnded;
  // The two facts only the engine witnesses that diagnostics needs: whether a
  // call is up (the one thing that stops an upload, docs/10 §5) and whether
  // signaling is connected (the "unusable for minutes" verdict, §4). Deduped,
  // because the engine emits on every state change and these two change rarely.
  let inCallNow = engine.state.get().call !== null;
  let signalingConnected = engine.state.get().signaling === 'connected';
  const detachCallLog = engine.state.subscribe(() => {
    const next = engine.state.get();
    const inCall = next.call !== null;
    if (inCall !== inCallNow) {
      inCallNow = inCall;
      // Through the gateway, not the centre: it holds the flag SYNCHRONOUSLY,
      // so a call that starts before storage has answered still stops an
      // upload (docs/10 §5).
      noteWebCall(inCall);
    }
    const connected = next.signaling === 'connected';
    if (connected !== signalingConnected) {
      signalingConnected = connected;
      // A dropped socket restarts the race clock, so the next `endpoint.won`
      // measures THAT round rather than the whole session.
      if (!connected) racingSince = Date.now();
      withCentre((centre) => centre.noteSignaling(connected));
    }
    if (next.lastEnded && next.lastEnded !== prevLastEnded) {
      void recordEndedCall(next.lastEnded);
    }
    prevLastEnded = next.lastEnded;
  });

  // Mirror the device-local unseen-missed count to the native launcher badge:
  // a newly-recorded missed call bumps it, opening Recents (markAllMissedSeen)
  // clears it — the same log the in-app Recents badge reads.
  const detachBadge = wireMissedBadge((count) => void pushMissedBadge(count));

  // Contact-alias cloud sync (docs/08 §8): keep the device-local alias store in
  // step with the account's E2E-encrypted blob. The key is derived from the
  // identity seed here (the one place the authed session holds it) and cached in
  // the driver; the plaintext aliases never leave the device. Pulls on start,
  // pushes debounced on every local edit (via onAliasesChanged), and reconciles
  // again whenever the browser comes back online.
  const aliasSync = new AliasSyncDriver({
    key: deriveAliasKey(identity.seed),
    rest: {
      getAliasesBlob: () => rest.getAliasesBlob(),
      putAliasesBlob: (blob) => rest.putAliasesBlob(blob),
    },
    store: {
      getMap: () => ({ ...getAliases() }),
      replaceMap: (map) => replaceAliases(map),
      subscribe: (cb) => onAliasesChanged(cb),
    },
    marker: {
      get: () => getAliasSyncMarker(),
      set: (updatedAt) => setAliasSyncMarker(updatedAt),
    },
  });
  const onOnlineResume = (): void => aliasSync.resume();
  window.addEventListener('online', onOnlineResume);
  aliasSync.start();

  // `net.change` (docs/10 §2): a report that says "everything failed" is worth
  // very little without "…and the link changed here". The browser states two
  // facts — `onLine` and `navigator.connection` — and both are edges, so the
  // event is cut on transition only and the device context follows it, exactly
  // as the native hosts do through their own net-change callback.
  const onNetworkChange = (): void => {
    const to = currentNetworkKind();
    withCentre((centre) => {
      if (centre.networkKind() === to) return;
      centre.record(netChange({ from: centre.networkKind(), to }));
      centre.setDeviceContext({ networkKind: to });
    });
  };
  const connection = (globalThis as { navigator?: { connection?: EventTarget } }).navigator
    ?.connection;
  window.addEventListener('online', onNetworkChange);
  window.addEventListener('offline', onNetworkChange);
  connection?.addEventListener?.('change', onNetworkChange);

  const screensDeps: ScreensDeps = { api: rest, engine, media, echoTest, liveEvents };

  let disposed = false;
  return {
    engine,
    media,
    echoTest,
    rest,
    liveEvents,
    screensDeps,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      detachCallLog();
      detachBadge();
      detachRemoteAudio();
      window.removeEventListener('online', onOnlineResume);
      window.removeEventListener('online', onNetworkChange);
      window.removeEventListener('offline', onNetworkChange);
      connection?.removeEventListener?.('change', onNetworkChange);
      // The centre itself is NOT stopped: it is process-wide, it outlives this
      // session's composition, and `DiagCenter.stop` is one-way.
      aliasSync.stop();
      engine.stop();
      media.releaseMic();
    },
  };
}

/**
 * The token minter of the most recent authenticated composition. Module-scoped
 * for the same reason `restOrigin.ts` keeps one router: the shell has exactly
 * one signed-in session at a time, and `ReadyApp` calls the refresh below with
 * the engine alone. `GET /endpoints` is Bearer-gated, so without this the origin
 * leg could not run at all.
 *
 * `services.test.ts` drives `refreshSignedEndpoints(engine)` through the real
 * one-argument call site after a real `createAppServices`, because this
 * assignment is the whole mechanism and a test that injects `getToken` proves
 * nothing about it.
 */
let sessionToken: AccessTokenProvider | null = null;

/**
 * Best-effort signed endpoint-list refresh (docs/04 §2), web edition.
 *
 * The implementation is the brain's — one `refreshEndpoints` for iOS, Android
 * and web, so the ordering (origin first, independent mirror only when the
 * origin cannot be reached at all), the bounding and the single
 * verify-then-adopt trust path cannot drift between platforms. This wrapper
 * only supplies what is web-shaped: the shell's origin router, its session
 * token and its build-time mirror URL.
 *
 * No mirror configured — which is every build today — is a no-op, exactly as it
 * has always been: the web app has never polled `/endpoints` on its own, and
 * turning that poll on is a separate decision from shipping the mirror.
 */
export async function refreshSignedEndpoints(
  engine: CallEngine,
  deps?: { getToken?: () => Promise<string>; fetchFn?: typeof fetch; mirrorUrl?: string },
): Promise<void> {
  const mirrorUrl = deps?.mirrorUrl ?? ENDPOINTS_MIRROR_URL;
  if (!mirrorUrl) return;
  const getToken = deps?.getToken ?? sessionToken;
  // No composed session ⇒ no refresh, rather than a refresh with a minter that
  // is certain to reject. FAIL CLOSED: a rejecting minter reads as "the origin
  // could not be reached", which is precisely the one outcome that spends the
  // mirror — so the bug shape here is "the web dials a third-party host on
  // every launch", the healthy-path cost the ordering rule exists to avoid.
  // There is no origin leg to fail, so there is no evidence the origin is
  // unreachable, so the mirror has not earned a request.
  if (!getToken) return;
  const origin = shellRestOrigin();
  // Bounded: this runs on launch, and under censorship the endpoint host is
  // exactly the one likeliest to blackhole rather than refuse (docs/04 §2).
  // The shell's decorated transport by default, so the healing poll leaves the
  // same `http.attempt` behind as every other request — a report that cannot
  // say whether `/endpoints` was reachable is missing the fact it exists for.
  const fetchFn = deps?.fetchFn ?? shellFetch;
  await refreshEndpoints({
    engine,
    // Read per attempt, like every other REST byte: the origin follows whatever
    // the signaling race won, so the healing poll is never pinned to the one
    // host it exists to route around.
    get apiBaseUrl(): string {
      return origin.base();
    },
    getToken,
    fetchFn: routeFailures(origin, fetchFn),
    mirrorUrl,
    // Not `routeFailures`-wrapped: a blocked mirror is not evidence about our
    // own origin and must never rotate the host a call is about to use.
    mirrorFetchFn: fetchFn,
  });
}
